import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useTokens, RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { getAccessibleTextOnBackground } from '../utils/luminance';

// ============================================================================
// Slot group definitions (inline literals — required by the schema linter)
// ============================================================================

const primaryCtaDef: SlotGroupDefinition = {
  label: 'Primary CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/contact-us' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

/**
 * Schedule a Tour page section for guided tours. Explains that guided tours
 * are by appointment with a leasing agent and CTAs to the Contact Us form
 * (single CRM entry point — no second form on this page).
 */
export function GuidedTourCTA() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const primaryCta = useSlotGroup('guidedtourcta.primary_cta', primaryCtaDef);

  const tokens = useTokens();
  // WCAG AA text for the primary brand surface — recomputed when the property
  // primary / textOnPrimary tokens change (light or dark brands).
  const textOnBg = useMemo(
    () => getAccessibleTextOnBackground(tokens?.color?.primary, tokens?.color?.textOnPrimary),
    [tokens],
  );

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up w-full bg-primary pt-[40px] pb-[60px] md:pt-12 md:pb-[80px]"
      style={{ '--gtc-fg': textOnBg } as React.CSSProperties}
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="guidedtourcta.heading"
          label="Heading"
          defaultValue="<p>Prefer A Guided Tour?</p>"
          as="h2"
          className="text-balance text-[clamp(2rem,8.8vw,3.5rem)] sm:text-[clamp(1rem,4.4vw,2.5rem)] uppercase tracking-wide leading-[1.1] mb-6"
          style={{ color: textOnBg, fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}
        />

        <div className="max-w-3xl mx-auto md:px-10 lg:px-0">
          <RichTextContent
            path="guidedtourcta.body"
            label="Body Text"
            defaultValue='<p>We also offer guided tours with a leasing agent. Because most of our communities do not have an on-site agent, guided tours are by appointment only so the agent can confirm availability and commute time. Prospects cannot schedule these online — submit our contact form and a leasing agent will follow up to arrange your visit to <span data-type="field-token" data-field="property_name"></span>.</p>'
            as="div"
            className="font-body text-base leading-relaxed [&_p]:mb-4 [&_a]:underline"
            style={{ color: textOnBg }}
          />
        </div>

        <div className="mt-8 flex items-center justify-center">
          <EditableSlotGroup groupPath="guidedtourcta.primary_cta" definition={primaryCtaDef}>
            <a
              href={primaryCta.url || '/contact-us'}
              target={primaryCta._target || '_self'}
              rel={primaryCta._target === '_blank' ? 'noopener noreferrer' : undefined}
              className="gtc-cta gtc-cta--outline relative inline-flex items-center justify-center whitespace-nowrap px-6 py-3 font-heading text-sm md:px-4 md:tracking-[0.1em] lg:px-12 lg:py-4 lg:text-base lg:tracking-[0.15em] uppercase tracking-[0.15em] overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{primaryCta.label || 'Contact Us'}</span>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        /* Outlined pill CTA — foreground tracks the computed contrast color. */
        .gtc-cta--outline {
          background: transparent;
          border: 1.5px solid color-mix(in srgb, var(--gtc-fg, var(--vibe-color-text-on-primary, #fff)) 75%, transparent);
          border-radius: 9999px;
          color: var(--gtc-fg, var(--vibe-color-text-on-primary, #fff));
          box-shadow: none;
        }
        .gtc-cta--outline::before {
          content: '';
          position: absolute;
          inset: 0;
          background: color-mix(in srgb, var(--gtc-fg, var(--vibe-color-text-on-primary, #fff)) 18%, transparent);
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .gtc-cta--outline:hover {
          border-color: var(--gtc-fg, var(--vibe-color-text-on-primary, #fff));
          color: var(--gtc-fg, var(--vibe-color-text-on-primary, #fff));
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0,0,0,0.15),
            0 4px 16px color-mix(in srgb, var(--gtc-fg, var(--vibe-color-text-on-primary, #fff)) 18%, transparent);
        }
        .gtc-cta--outline:hover::before {
          opacity: 1;
        }
        .gtc-cta--outline:active {
          transform: translateY(0);
          box-shadow: none;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'GuidedTourCTA',
  displayName: 'Guided Tour CTA',
  tokens: {
    'guidedtourcta.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Prefer A Guided Tour?</p>',
    },
    'guidedtourcta.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultValue:
        '<p>We also offer guided tours with a leasing agent. Because most of our communities do not have an on-site agent, guided tours are by appointment only so the agent can confirm availability and commute time. Prospects cannot schedule these online — submit our contact form and a leasing agent will follow up to arrange your visit to <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
  },
  slotGroups: {
    'guidedtourcta.primary_cta': primaryCtaDef,
  },
};

(GuidedTourCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default GuidedTourCTA;
